/**
 * Missed Captures Calculator
 * Calculates expected capture slots and identifies missed captures
 */

import type {
  Photo,
  TimerSettings,
  TimerSettingsHistoryEntry,
  MissedCapture,
  TimeRange,
  DaySchedule,
  DisabledPeriod,
} from "@/types";

interface CalculateMissedCapturesParams {
  photos: Photo[];
  timerSettings: TimerSettings;
  settingsHistory: TimerSettingsHistoryEntry[];
  toleranceSeconds: number;
  rangeStart: Date;
  rangeEnd: Date;
  captureOrigin?: Date; // From Android's captureStartEpochMillis for exact alignment
  disabledPeriods?: DisabledPeriod[]; // Periods when captures were intentionally disabled
}

/**
 * Check if a time is within any of the time ranges
 */
function isWithinTimeRanges(date: Date, timeRanges: TimeRange[]): boolean {
  if (!timeRanges || timeRanges.length === 0) return true;

  const hours = date.getHours();
  const minutes = date.getMinutes();
  const timeInMinutes = hours * 60 + minutes;

  return timeRanges.some((range) => {
    const [startH, startM] = range.start.split(":").map(Number);
    const [endH, endM] = range.end.split(":").map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    // Handle overnight ranges (e.g., 22:00 - 06:00)
    if (endMinutes < startMinutes) {
      return timeInMinutes >= startMinutes || timeInMinutes < endMinutes;
    }

    return timeInMinutes >= startMinutes && timeInMinutes < endMinutes;
  });
}

/**
 * Check if a date should have a capture based on schedule settings
 */
function isWithinSchedule(date: Date, settings: TimerSettings): boolean {
  // No schedule restriction means every slot is valid
  if (!settings.scheduleEnabled) return true;

  const dayOfWeek = date.getDay(); // 0 = Sunday

  // Advanced mode: per-day schedules
  if (settings.scheduleMode === "advanced" && settings.dailySchedules) {
    const daySchedule = settings.dailySchedules.find((d: DaySchedule) => d.day === dayOfWeek);
    if (!daySchedule || !daySchedule.enabled) return false;
    return isWithinTimeRanges(date, daySchedule.timeRanges);
  }

  // Basic mode: shared time ranges for selected days
  if (settings.daysOfWeek && !settings.daysOfWeek.includes(dayOfWeek)) {
    return false;
  }

  return isWithinTimeRanges(date, settings.timeRanges || []);
}

/**
 * Check if a timestamp falls within any disabled period
 * Uses binary search for efficiency with large numbers of periods
 */
function isWithinDisabledPeriod(timestamp: Date, disabledPeriods: DisabledPeriod[]): boolean {
  if (!disabledPeriods || disabledPeriods.length === 0) return false;

  const time = timestamp.getTime();

  for (const period of disabledPeriods) {
    if (time >= period.start.getTime() && time < period.end.getTime()) {
      return true;
    }
  }

  return false;
}

/**
 * Generate expected capture slots within a time range
 * Handles settings history by generating slots per-period with correct interval
 */
function generateExpectedSlots(
  rangeStart: Date,
  rangeEnd: Date,
  timerSettings: TimerSettings,
  settingsHistory: TimerSettingsHistoryEntry[],
  captureOrigin?: Date  // From Android - actual capture cycle start time
): Date[] {
  const slots: Date[] = [];
  const startMs = rangeStart.getTime();
  const endMs = rangeEnd.getTime();
  const captureOriginMs = captureOrigin?.getTime();

  // Sort history by changedAt ascending (oldest first)
  const sortedHistory = [...settingsHistory].sort((a, b) => {
    const aTime = a.changedAt?.toDate?.()?.getTime() || 0;
    const bTime = b.changedAt?.toDate?.()?.getTime() || 0;
    return aTime - bTime;
  });

  // Build time periods with their settings
  // Each period: [periodStart, periodEnd, settings, anchorTime]
  interface Period {
    start: number;
    end: number;
    interval: number;
    anchor: number;
    settings: TimerSettings;
  }

  const periods: Period[] = [];

  if (sortedHistory.length === 0) {
    // No history - use current settings for entire range
    // Use captureOrigin if available, otherwise fall back to rangeStart
    periods.push({
      start: startMs,
      end: endMs,
      interval: (timerSettings.interval || 60) * 1000,
      anchor: captureOriginMs || startMs,
      settings: timerSettings,
    });
  } else {
    // Build periods from history
    for (let i = 0; i < sortedHistory.length; i++) {
      const entry = sortedHistory[i];
      const entryTime = entry.changedAt?.toDate?.()?.getTime() || 0;
      const nextEntryTime = i < sortedHistory.length - 1
        ? sortedHistory[i + 1].changedAt?.toDate?.()?.getTime() || endMs
        : endMs;

      // Only include periods that overlap with our range
      const periodStart = Math.max(entryTime, startMs);
      const periodEnd = Math.min(nextEntryTime, endMs);

      if (periodStart < periodEnd) {
        // For the LAST period (current capture cycle), use captureOrigin if available
        // This ensures slots align with actual Android capture times
        const isLastPeriod = i === sortedHistory.length - 1;
        const anchor = (isLastPeriod && captureOriginMs && captureOriginMs >= entryTime)
          ? captureOriginMs
          : entryTime;

        periods.push({
          start: periodStart,
          end: periodEnd,
          interval: (entry.interval || 60) * 1000,
          anchor: anchor,
          settings: {
            interval: entry.interval,
            // History records written before the rename carry `alwaysActive`, the
            // negation of this field. Both shapes stay readable; old records are
            // never rewritten.
            scheduleEnabled: entry.scheduleEnabled ?? !entry.alwaysActive,
            daysOfWeek: entry.daysOfWeek,
            timeRanges: entry.timeRanges,
            scheduleMode: entry.scheduleMode,
            dailySchedules: entry.dailySchedules,
          },
        });
      }
    }

    // Handle time before first history entry
    // Skip generating slots for this period - we don't have reliable interval data
    // The UI should show photos only (no missed captures) for pre-history period
  }

  // Generate slots for each period
  for (const period of periods) {
    const { start, end, interval, anchor, settings } = period;

    // Align to anchor: find first slot >= period.start
    const intervalsFromAnchor = Math.ceil((start - anchor) / interval);
    let currentTime = anchor + intervalsFromAnchor * interval;

    while (currentTime <= end) {
      const slotDate = new Date(currentTime);

      if (currentTime >= start && isWithinSchedule(slotDate, settings)) {
        slots.push(slotDate);
      }

      currentTime += interval;
    }
  }

  // Sort slots chronologically
  slots.sort((a, b) => a.getTime() - b.getTime());

  return slots;
}

/**
 * Find if a photo exists for an expected slot (within tolerance)
 */
function findMatchingPhoto(
  expectedTime: Date,
  photos: Photo[],
  toleranceMs: number
): Photo | null {
  const expectedMs = expectedTime.getTime();

  for (const photo of photos) {
    const capturedAt = photo.capturedAt?.toDate?.() || new Date(0);
    const diff = Math.abs(capturedAt.getTime() - expectedMs);
    if (diff <= toleranceMs) {
      return photo;
    }
  }

  return null;
}

/**
 * Calculate missed captures based on photos, timer settings, and schedule
 * Excludes slots that fall within disabled periods (when user intentionally stopped captures)
 */
export function calculateMissedCaptures({
  photos,
  timerSettings,
  settingsHistory,
  toleranceSeconds,
  rangeStart,
  rangeEnd,
  captureOrigin,
  disabledPeriods,
}: CalculateMissedCapturesParams): MissedCapture[] {
  const missedCaptures: MissedCapture[] = [];
  const toleranceMs = toleranceSeconds * 1000;
  const now = new Date();

  // Find anchor timestamp - priority:
  // 1. captureOrigin from Android (exact match with app's capture cycle)
  // 2. Settings history changedAt (fallback)
  // 3. Photo median offset (last resort)
  let anchorTimestamp: Date | undefined;

  // Priority 1: Use captureOrigin from Android if available
  // This is the exact moment the capture cycle started on the device
  if (captureOrigin) {
    anchorTimestamp = captureOrigin;
  }

  // Priority 2: Fall back to settings history
  if (!anchorTimestamp) {
    // Sort history by changedAt descending to find most recent settings change
    const sortedHistory = [...settingsHistory].sort((a, b) => {
      const aTime = a.changedAt?.toDate?.() || new Date(0);
      const bTime = b.changedAt?.toDate?.() || new Date(0);
      return bTime.getTime() - aTime.getTime();
    });

    if (sortedHistory.length > 0) {
      anchorTimestamp = sortedHistory[0].changedAt?.toDate?.();
    }
  }

  // Priority 3: Fallback to photo median offset
  if (!anchorTimestamp && photos.length > 0) {
    const intervalMs = (timerSettings.interval || 60) * 1000;

    // Get recent photos to calculate median offset within the interval cycle
    const sortedPhotos = [...photos].sort((a, b) => {
      const aTime = a.capturedAt?.toDate?.() || new Date(0);
      const bTime = b.capturedAt?.toDate?.() || new Date(0);
      return bTime.getTime() - aTime.getTime(); // Most recent first
    });

    const recentPhotos = sortedPhotos.slice(0, 20);
    const offsets = recentPhotos.map(p => {
      const time = p.capturedAt?.toDate?.()?.getTime() || 0;
      return time % intervalMs;
    });

    // Find median offset (represents the "true" cycle position)
    offsets.sort((a, b) => a - b);
    const medianOffset = offsets[Math.floor(offsets.length / 2)];

    // Create anchor: use most recent photo adjusted to median offset
    const mostRecentTime = sortedPhotos[0].capturedAt?.toDate?.()?.getTime() || 0;
    const currentOffset = mostRecentTime % intervalMs;
    let adjustment = medianOffset - currentOffset;

    // Handle wrap-around
    if (adjustment < -intervalMs / 2) {
      adjustment += intervalMs;
    } else if (adjustment > intervalMs / 2) {
      adjustment -= intervalMs;
    }

    anchorTimestamp = new Date(mostRecentTime + adjustment);
  }

  // Generate expected slots aligned to actual capture cycle
  const expectedSlots = generateExpectedSlots(rangeStart, rangeEnd, timerSettings, settingsHistory, anchorTimestamp);

  // Check each expected slot
  for (const expectedTime of expectedSlots) {
    // Skip future slots (beyond current time)
    if (expectedTime.getTime() > now.getTime()) {
      continue;
    }

    // Skip slots that fall within disabled periods (user intentionally stopped captures)
    if (disabledPeriods && isWithinDisabledPeriod(expectedTime, disabledPeriods)) {
      continue;
    }

    // Check if we have a photo for this slot
    const matchingPhoto = findMatchingPhoto(expectedTime, photos, toleranceMs);

    if (!matchingPhoto) {
      // No photo found - determine if pending or missed
      const timeSinceExpected = now.getTime() - expectedTime.getTime();
      const status: "pending" | "missed" =
        timeSinceExpected <= toleranceMs ? "pending" : "missed";

      missedCaptures.push({
        expectedAt: expectedTime,
        status,
        // nearestStatusSnapshot will be added by useMissedCaptures hook
      });
    }
  }

  return missedCaptures;
}

/**
 * Calculate the default time range for missed captures analysis
 * Returns last 24 hours or since first photo if fewer photos
 */
export function getDefaultAnalysisRange(photos: Photo[]): { start: Date; end: Date } {
  const now = new Date();
  const defaultStart = new Date(now.getTime() - 24 * 60 * 60 * 1000); // 24 hours ago

  if (photos.length === 0) {
    return { start: defaultStart, end: now };
  }

  // Sort photos by capturedAt ascending
  const sortedPhotos = [...photos].sort((a, b) => {
    const aTime = a.capturedAt?.toDate?.() || new Date(0);
    const bTime = b.capturedAt?.toDate?.() || new Date(0);
    return aTime.getTime() - bTime.getTime();
  });

  // Use first photo time or 24h ago, whichever is more recent
  const firstPhotoTime = sortedPhotos[0].capturedAt?.toDate?.() || defaultStart;
  const rangeStart = firstPhotoTime.getTime() > defaultStart.getTime() ? firstPhotoTime : defaultStart;

  return { start: rangeStart, end: now };
}

/**
 * Merge photos and missed captures into a unified sorted array
 * for display in grid/list views
 */
export type GalleryItem =
  | { type: "photo"; data: Photo; timestamp: Date }
  | { type: "missed"; data: MissedCapture; timestamp: Date };

export function mergePhotosAndMissedCaptures(
  photos: Photo[],
  missedCaptures: MissedCapture[]
): GalleryItem[] {
  const items: GalleryItem[] = [];

  // Add photos
  for (const photo of photos) {
    const timestamp = photo.capturedAt?.toDate?.() || new Date(0);
    items.push({ type: "photo", data: photo, timestamp });
  }

  // Add missed captures
  for (const missed of missedCaptures) {
    items.push({ type: "missed", data: missed, timestamp: missed.expectedAt });
  }

  // Sort by timestamp descending (most recent first)
  items.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  return items;
}
